/**
 * Checks if a type is an object with dynamic keys
 * (like {[key: string]: any} or Record<string, any>)
 * Excludes mapped types with enum/literal keys, even if optional
 */
type IsDynamicObject<T> = T extends Record<PropertyKey, any>
  ? string extends keyof T
    ? true
    : false
  : false

/**
 * Extracts the key type from a Record object
 * Returns 'never' if it's not a Record
 */
type ExtractRecordKeys<T> = T extends Record<infer K, any> ? K : never

/**
 * Checks if the extracted key type is actually string
 * (necessary because Object.keys() only returns strings)
 */
type AreKeysString<K> = K extends string ? true : false

/**
 * Determines the appropriate return type for getObjectKeys
 * based on the object structure
 */
type GetKeysReturnType<T extends object> = IsDynamicObject<T> extends true
  ? ExtractRecordKeys<T> extends infer K
    ? K extends never
      ? string[] // Fallback if extraction fails
      : AreKeysString<K> extends true
      ? K[] // Exact key type if they are strings
      : string[] // Fallback if keys are not strings
    : string[] // Fallback if inference fails
  : (keyof T)[] // Fixed structure object

export const getObjectKeys = <T extends object>(
  obj: T
): GetKeysReturnType<T> => {
  // eslint-disable-next-line no-restricted-properties
  return Object.keys(obj) as any
}

export const tuple = <T>(value: [T, T]) => value

export const buildObjectFromEntries = <
  K extends string,
  V,
  Complete extends boolean = false
>(
  entries: [K, V][],
  // Complete is only used for type inference
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  complete?: Complete
): string extends K
  ? { [key: string]: V }
  : Complete extends true
  ? { [P in K]: V }
  : { [P in K]?: V } => {
  // eslint-disable-next-line no-restricted-properties
  return Object.fromEntries(entries) as any
}

export type PropsOf<C> = C extends new (...args: any[]) => { $props: infer P }
  ? P
  : never

export const filterNullValue = <T>(array: T[]): NonNullable<T>[] => {
  return array.filter(
    item => item !== null && item !== undefined
  ) as NonNullable<T>[]
}
